import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, List, ListItem, Stack } from '@mui/material';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { APP, ROUTES } from 'config';
import {
  expenseStatuses,
  useExpenses,
  COLOR_BY_STATUS,
  useExportToXls,
  useUpdateExpenseStatusBulk,
  expensesFiltersSchema,
} from 'store/expenses';
import { shopItemCategories } from 'types/MarketplaceItems';
import moment from 'moment';
import { formatCurrency, formatList } from 'utils/format';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { DownloadXlsAction } from 'components/actions';
import {
  DateRangeFilter,
  EmployeesFilter,
  FilterButton,
  Filters,
  SourcesFilter,
  SelectFilter,
  OpenSearchFilter,
  OrgStructureFilter,
} from 'components/filters';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import {
  CreateExpenseModal,
  EditExpenseModal,
} from 'containers/employees/employee-wallet/atoms';
import { useModalContext } from 'store/modal';
import { fromCents } from 'utils/general';
import { useSearchParams } from 'utils/navigation';
import { useRouteMatch } from 'react-router-dom';
import { usePredefinedToasts } from 'utils/Toast';
import { EmployeeCell } from 'components/table';

export const ExternalExpensesList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { path } = useRouteMatch();
  const { hasEditPermission, hasViewPermission } = useMe();
  const { currency } = useDefaultCurrency();
  const predefinedToasts = usePredefinedToasts();

  const { searchParams: filters, navigate } = useSearchParams(
    expensesFiltersSchema,
  );

  const { data: expenses, isLoading } = useExpenses({
    ...filters,
    type: ['manual'],
  });

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: updateStatusBulk, isLoading: isUpdating } =
    useUpdateExpenseStatusBulk();

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('expenses.filters.type.options.manual'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'external-expenses',
                label: t('expenses.filters.type.options.manual'),
                url: ROUTES.expenses.external.list,
                isActive: path === ROUTES.expenses.external.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('benefitExpenses.addExpense'),
                      color: 'primary',
                      disabled:
                        !hasEditPermission(['expenses']) ||
                        !hasViewPermission(['employees']),
                      onClick: () => {
                        openModal({
                          component: CreateExpenseModal,
                          size: 'lg',
                        });
                      },
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={expenses?.total} />

                <Stack direction="row" alignItems="center" gap={1}>
                  <Filters
                    value={filters}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                    initialValues={expensesFiltersSchema.parse({})}
                    onChangeCallback={() => setSelectedRowIds({})}
                  >
                    {({ value, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('home.organisationStructure_title')}
                          value={value.orgStructure}
                          initialValue={initialValues.orgStructure}
                          onChange={(v) => setFilter('orgStructure', v)}
                          contentMaxWidth={420}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <OrgStructureFilter
                              value={value}
                              onApplyFilter={applyFilter}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        {hasViewPermission(['employees']) ? (
                          <FilterButton
                            label={t('entity_type.employee')}
                            value={value.employees}
                            initialValue={initialValues.employees}
                            onChange={(v) => setFilter('employees', v)}
                          >
                            {({ value, applyFilter, clearFilter, close }) => (
                              <EmployeesFilter
                                value={{ value }}
                                onApplyFilter={({ value }) =>
                                  applyFilter(value)
                                }
                                onClearFilter={clearFilter}
                                onCancel={close}
                              />
                            )}
                          </FilterButton>
                        ) : null}

                        <FilterButton
                          label={t('marketplaceOrders.orderDate')}
                          value={value.createdAt}
                          initialValue={initialValues.createdAt}
                          onChange={(v) => setFilter('createdAt', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.expense_date')}
                          value={value.date}
                          initialValue={initialValues.date}
                          onChange={(v) => setFilter('date', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('wallet.employeeWalletFundsSource')}
                          value={value.source}
                          initialValue={initialValues.source}
                          onChange={(v) => setFilter('source', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SourcesFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.filters.categories.label')}
                          value={value.categories}
                          initialValue={initialValues.categories}
                          onChange={(v) => setFilter('categories', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={shopItemCategories.map((category) => ({
                                key: category,
                                label: t(
                                  `marketplaceItems.itemCategory-${category}`,
                                ),
                              }))}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.filters.status.label')}
                          value={value.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={expenseStatuses.map((status) => ({
                                key: status,
                                label: t(`expenses.status.${status}`),
                              }))}
                              searchable={false}
                            />
                          )}
                        </FilterButton>

                        {hasViewPermission(['employees']) ? (
                          <OpenSearchFilter
                            value={value.search}
                            onApplyFilter={(v) => setFilter('search', v)}
                            placeholder={t(
                              'expenses.filters.search.placeholder',
                            )}
                          />
                        ) : null}
                      </>
                    )}
                  </Filters>

                  <Divider orientation="vertical" variant="middle" flexItem />

                  <DownloadXlsAction
                    listLength={expenses?.total ?? 0}
                    exportToExcel={() => exportToXls({ filters })}
                    loading={isExportingToXls}
                    limitedRange={
                      filters.date
                        ? {
                            limit: 1,
                            unit: 'year',
                            dateFrom: filters.date?.from,
                            dateTo: filters.date?.to,
                          }
                        : {
                            limit: 1,
                            unit: 'year',
                            dateFrom: filters.createdAt?.from,
                            dateTo: filters.createdAt?.to,
                          }
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={expenses?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'employee',
                header: t('entity_type.employee'),
                cell: ({ row: { original: expense } }) => (
                  <EmployeeCell employee={expense.employee} />
                ),
              },
              {
                id: 'date',
                header: t('marketplaceOrders.orderDate'),
                cell: ({ row: { original: expense } }) =>
                  moment(expense.createdAt).format('l'),
              },
              {
                id: 'expenseDate',
                header: t('expenses.expense_date'),
                cell: ({ row: { original: expense } }) =>
                  expense.date ? moment(expense.date).format('l') : '-',
              },
              {
                id: 'source',
                header: t('wallet.employeeWalletFundsSource'),
                cell: ({ row: { original: expense } }) => (
                  <List sx={{ py: 0 }}>
                    {expense.source.map((s) => (
                      <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
                        {s.id === RECOGNITION_SHOP_ID ? (
                          hasViewPermission(['employeeRecognition']) ? (
                            <Link
                              label={s.name}
                              to={ROUTES.recognitions.settings}
                            />
                          ) : (
                            <Typography>{s.name}</Typography>
                          )
                        ) : hasViewPermission(['benefits']) ? (
                          <Link
                            label={s.name}
                            to={ROUTES.benefits.details.replace(':id', s.id)}
                          />
                        ) : (
                          <Typography>{s.name}</Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ),
              },
              {
                id: 'description',
                header: t('expenses.table.description.title'),
                cell: ({ row: { original: expense } }) => expense.description,
              },
              {
                id: 'categories',
                header: t('expenses.table.categories.title'),
                cell: ({ row: { original: expense } }) =>
                  formatList(
                    i18n.language,
                    expense.categories.map((category) =>
                      t(`marketplaceItems.itemCategory-${category}`),
                    ),
                  ),
              },
              {
                id: 'price',
                header: t('employees.reserved'),
                meta: { align: 'right' },
                cell: ({ row: { original: expense } }) =>
                  formatCurrency(i18n.language, expense.amount, currency),
              },
              {
                id: 'status',
                header: t('expenses.table.status.title'),
                cell: ({ row: { original: expense } }) => (
                  <Typography color={COLOR_BY_STATUS[expense.status]}>
                    {t(`expenses.status.${expense.status}`)}
                  </Typography>
                ),
              },
              {
                id: 'actions',
                cell: ({ row: { original: expense } }) => {
                  if (expense.type !== 'manual') return null;

                  return (
                    <ActionsMenu
                      type="compact"
                      items={[
                        {
                          label: t('common.edit'),
                          disabled: !hasEditPermission(['expenses']),
                          onClick: () => {
                            openModal({
                              component: EditExpenseModal,
                              props: {
                                expenseId: expense.id,
                                employeeId: expense.employee?.id ?? '',
                                values: {
                                  date: expense.date,
                                  status: expense.status,
                                  amount: fromCents(expense.amount),
                                  benefits: expense.source
                                    .filter(
                                      (source) =>
                                        source.id !== RECOGNITION_SHOP_ID,
                                    )
                                    .map((source) => source.id),
                                  useRecognition: !!expense.source.filter(
                                    (source) =>
                                      source.id === RECOGNITION_SHOP_ID,
                                  ).length,
                                  categories: expense.categories,
                                  description: expense.description,
                                },
                              },
                              size: 'lg',
                            });
                          },
                        },
                      ]}
                    />
                  );
                },
              },
            ]}
            pagination={
              expenses && {
                count: expenses.total,
                page: expenses.page,
                pageSize: expenses.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
            selection={{
              value: selectedRowIds,
              onChange: setSelectedRowIds,
              isLoading: isUpdating,
              actions: (['paid', 'completed'] as const).map((status) => ({
                label: t('global.actions.mark_as', {
                  status: t(`status.${status}`),
                }),
                disabled: !hasEditPermission(['expenses']),
                onClick: () => {
                  updateStatusBulk(
                    {
                      expenses: Object.keys(selectedRowIds).map(
                        (expenseId) => ({
                          id: expenseId,
                          status,
                        }),
                      ),
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                },
              })),
            }}
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
